// @file Surface reactions store
import appCan from '@@/bits/app_can'
import device from '@@/bits/device'
import { captureNonNetworkFetchError } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import window from '@@/bits/global'
import { getVuexStore } from '@@/bits/pinia'
import { unboundedWatch, vSet } from '@@/bits/vue'
import { JsonAPIResource, Reaction, ReactionType } from '@padlet/arvo'
import { sortBy } from 'lodash-es'
import { openPostReactionDialog } from 'native_bridge/actions'
import postMessage from 'native_bridge/post_message'
import { defineStore, storeToRefs } from 'pinia'
import { useGlobalSnackbarStore } from 'pinia/global_snackbar'
import { useSurfaceStore } from 'pinia/surface'
import { useSurfacePermissionsStore } from 'pinia/surface_permissions'
import { useSurfaceSettingsStore } from 'pinia/surface_settings'
import PadletApi from 'surface/padlet_api'
import { AccumulatedReactionData, AccumulatedReactions, Id, WallReactionData } from 'types'
import { computed, ref } from 'vue'

export const useReactionsStore = defineStore('reactions', () => {
  const surfaceVuexStore = window.app?.$store // For gradual conversion to pinia

  const { previewAttributes } = storeToRefs(useSurfaceSettingsStore())
  const { isReactable } = storeToRefs(useSurfacePermissionsStore())

  const originalReactionData = computed<WallReactionData>(
    () => surfaceVuexStore?.state.wall.reaction_data ?? { type: ReactionType.None },
  )
  const currentReactionData = computed<WallReactionData>(
    () => previewAttributes.value.reaction_data ?? { type: ReactionType.None },
  )
  const currentReactionType = computed(() => (!isReactable.value ? ReactionType.None : currentReactionData.value.type))

  /**
   * FETCH REACTIONS
   */
  const { wallId } = storeToRefs(useSurfaceStore())
  const { genericFetchError } = useGlobalSnackbarStore()
  const accumulatedReactionsByWishId = ref<AccumulatedReactions>({})
  const postIdBeingReactedTo = ref()
  const reactionDropdownTop = ref()
  const reactionDropdownLeft = ref()
  const reactionDropdownWidth = ref()
  const hasReactions = computed<boolean>(() => {
    const wishesWithReactions = Object.keys(accumulatedReactionsByWishId.value)
    if (wishesWithReactions.length === 0) return false
    return wishesWithReactions.some((wishId) => accumulatedReactionsByWishId.value[wishId].totalReactionsCount > 0)
  })

  if (isAppUsing('realtimeFetching')) {
    void unboundedWatch(
      () => getVuexStore()?.getters['reaction/accumulatedReactionsByWishId'],
      (newValue) => {
        accumulatedReactionsByWishId.value = { ...accumulatedReactionsByWishId.value, ...newValue }
        void getVuexStore()?.dispatch('nativeApp/postSurfaceState', null, { root: true })
      },
      { immediate: true },
    )
  }

  const fetchAccumulatedReactions = async (): Promise<void> => {
    try {
      let attributes
      if (isAppUsing('realtimeFetching')) {
        attributes = await getVuexStore()?.dispatch('realtime/fetchAccumulatedReactions', null, { root: true })
      } else {
        const fetchedData = await PadletApi.Reaction.fetchAccumulatedReactions({ wallId: wallId.value })
        const { data } = fetchedData
        if (data == null) return
        attributes = (data as JsonAPIResource<AccumulatedReactions>).attributes
      }

      accumulatedReactionsByWishId.value = attributes ?? {}
      void getVuexStore()?.dispatch('nativeApp/postSurfaceState', null, { root: true })
    } catch (error: any) {
      captureNonNetworkFetchError(error, { source: 'fetchAccumulatedReactions' })
      genericFetchError()
    }
  }

  const accumulatedReactionsTotalSumAverage = computed(() => {
    const reactionWishIds = Object.keys(accumulatedReactionsByWishId.value)
    if (reactionWishIds.length === 0) return 0
    const sum = reactionWishIds.reduce((acc, wishId) => {
      const reactionSum: number = accumulatedReactionsByWishId.value[wishId]?.sum ?? 0
      return acc + reactionSum
    }, 0)
    const totalReactions = reactionWishIds.reduce((acc, wishId) => {
      const reactionsTotal: number = accumulatedReactionsByWishId.value[wishId]?.totalReactionsCount ?? 0
      return acc + reactionsTotal
    }, 0)
    return sum / totalReactions
  })

  const accumulatedReactionsConfidenceNumber = computed(() => {
    // the totalReactions of the median reaction in all reactions for a wall
    // we choose median to be safe can always update if needed
    const reactionsList = Object.values(accumulatedReactionsByWishId.value).filter(
      (r) => r != null,
    ) as AccumulatedReactionData[]
    const numReactions = reactionsList.length
    const sortedReactionList = sortBy(reactionsList, 'totalReactionsCount')
    return sortedReactionList[Math.floor(numReactions / 2)]?.totalReactionsCount
  })

  /** REALTIME HANDLERS */

  function updateAccumulatedReactions(payload: {
    wishId: number
    sumChange: number
    totalReactionCountChange: number
    reactionType: ReactionType
  }): void {
    const { wishId, sumChange, totalReactionCountChange, reactionType } = payload
    const currentAccumulatedReactions = accumulatedReactionsByWishId.value?.[wishId] ?? {
      sum: 0,
      totalReactionsCount: 0,
      reactionType: originalReactionData.value.type,
    }
    vSet(accumulatedReactionsByWishId.value, wishId, {
      ...currentAccumulatedReactions,
      sum: currentAccumulatedReactions.sum + sumChange,
      totalReactionsCount: currentAccumulatedReactions.totalReactionsCount + totalReactionCountChange,
      reactionType,
    })
    void getVuexStore()?.dispatch('nativeApp/postSurfaceState', null, { root: true })
  }

  function updateUserReaction(payload: Partial<Reaction>): void {
    const wishId = payload.wish_id
    if (wishId == null) return
    const currentAccumulatedReactions = accumulatedReactionsByWishId.value?.[wishId] ?? {
      sum: 0,
      totalReactionsCount: 0,
      reactionType: originalReactionData.value.type,
    }
    vSet(accumulatedReactionsByWishId.value, wishId, {
      ...currentAccumulatedReactions,
      userReaction: payload.id != null ? (payload as Reaction) : undefined,
      reactionType: payload.reaction_type ?? originalReactionData.value.type,
    })
    void getVuexStore()?.dispatch('nativeApp/postSurfaceState', null, { root: true })
  }

  /** ACTIONS */

  const createReaction = async (payload: { postId: Id; value: number }): Promise<void> => {
    const { postId, value } = payload
    const newReaction = {
      wish_id: postId,
      value,
      reaction_type: originalReactionData.value.type,
    }
    try {
      const reactionData = await PadletApi.Reaction.create(newReaction)
      const resultingReaction = (reactionData?.data as JsonAPIResource<Reaction>).attributes
      const oldAccumulatedReactions = accumulatedReactionsByWishId.value[postId] ?? {
        sum: 0,
        totalReactionsCount: 0,
        reactionType: originalReactionData.value.type,
      }

      const hasReactionTypeChanged = oldAccumulatedReactions.reactionType !== resultingReaction.reaction_type

      const newAccumulatedReactions = {
        sum: resultingReaction.value + (hasReactionTypeChanged ? 0 : oldAccumulatedReactions.sum),
        totalReactionsCount: 1 + (hasReactionTypeChanged ? 0 : oldAccumulatedReactions.totalReactionsCount),
        reactionType: resultingReaction.reaction_type,
        userReaction: resultingReaction,
      }
      vSet(accumulatedReactionsByWishId.value, postId, newAccumulatedReactions)
    } catch (error: any) {
      genericFetchError()
      captureNonNetworkFetchError(error, { source: 'createReaction' })
    }
  }

  const updateReaction = async (payload: { postId: Id; value: number }): Promise<void> => {
    const { postId, value } = payload
    const oldAccumulatedReactions = accumulatedReactionsByWishId.value[postId]
    const userReaction = accumulatedReactionsByWishId.value[postId]?.userReaction
    if (
      oldAccumulatedReactions == null ||
      userReaction == null ||
      userReaction.value === value ||
      userReaction.reaction_type !== originalReactionData.value.type
    )
      return
    const existingReactionId = userReaction.id
    try {
      const reactionData = await PadletApi.Reaction.update({ id: existingReactionId, value })
      const resultingReaction = (reactionData?.data as JsonAPIResource<Reaction>).attributes
      const newAccumulatedReactions = {
        sum: oldAccumulatedReactions.sum - userReaction.value + resultingReaction.value,
        totalReactionsCount: oldAccumulatedReactions.totalReactionsCount,
        reactionType: resultingReaction.reaction_type,
        userReaction: resultingReaction,
      }
      vSet(accumulatedReactionsByWishId.value, postId, newAccumulatedReactions)
    } catch (error: any) {
      genericFetchError()
      captureNonNetworkFetchError(error, { source: 'deleteReaction' })
    }
  }

  const deleteReaction = async (payload: { postId: Id }): Promise<void> => {
    const { postId } = payload
    const oldAccumulatedReactions = accumulatedReactionsByWishId.value[postId]
    const userReaction = accumulatedReactionsByWishId.value[postId]?.userReaction
    if (
      oldAccumulatedReactions == null ||
      userReaction == null ||
      userReaction.reaction_type !== originalReactionData.value.type
    )
      return
    const existingReactionId = userReaction.id
    try {
      await PadletApi.Reaction.delete({ id: existingReactionId })
      const newAccumulatedReactions = {
        sum: oldAccumulatedReactions.sum - userReaction.value,
        totalReactionsCount: oldAccumulatedReactions.totalReactionsCount - 1,
        reactionType: originalReactionData.value.type,
        userReaction: undefined,
      }
      vSet(accumulatedReactionsByWishId.value, postId, newAccumulatedReactions)
    } catch (error: any) {
      genericFetchError()
      captureNonNetworkFetchError(error, { source: 'deleteReaction' })
    }
  }

  const startEditingReaction = async (payload: {
    postId: Id
    dropdownLeft?: number
    dropdownWidth?: number
    dropdownTop?: number
  }): Promise<void> => {
    if (device.app && appCan('useNativeReactionDialog')) {
      // update surface state right before editing to make sure reaction data is up to date
      await getVuexStore()?.dispatch('nativeApp/postSurfaceState', null, { root: true })
      postMessage(openPostReactionDialog(payload.postId))
    } else {
      postIdBeingReactedTo.value = payload.postId
      reactionDropdownLeft.value = payload.dropdownLeft ?? 0
      reactionDropdownWidth.value = payload.dropdownWidth ?? 0
      reactionDropdownTop.value = payload.dropdownTop ?? 0
    }
  }

  const stopEditingReaction = (): void => {
    postIdBeingReactedTo.value = undefined
    reactionDropdownLeft.value = 0
    reactionDropdownWidth.value = 0
    reactionDropdownTop.value = 0
  }

  return {
    // state
    accumulatedReactionsByWishId,
    postIdBeingReactedTo,
    reactionDropdownLeft,
    reactionDropdownTop,
    reactionDropdownWidth,
    // getters
    hasReactions,
    originalReactionData,
    currentReactionData,
    currentReactionType,
    accumulatedReactionsTotalSumAverage,
    accumulatedReactionsConfidenceNumber,

    // action
    fetchAccumulatedReactions,
    updateAccumulatedReactions,
    updateUserReaction,
    createReaction,
    updateReaction,
    deleteReaction,
    startEditingReaction,
    stopEditingReaction,
  }
})
